// src/components/preferences/CareerGoalsPreferenceEditor.tsx
/**
 * Editor for career goals preferences (short-term, long-term, growth areas)
 * ⚠️ FIXED: Uses correct field names matching backend schema
 */
import React from 'react';
import {
  IonItem,
  IonLabel,
  IonTextarea,
  IonList,
  IonChip,
  IonIcon,
  IonInput,
  IonButton,
} from '@ionic/react';
import { closeCircle } from 'ionicons/icons';
import { CareerGoals } from '../../types/preferences.ts';
import './PreferenceEditor.css';

interface CareerGoalsPreferenceEditorProps {
  data: CareerGoals;
  onChange: (data: CareerGoals) => void;
}

const CareerGoalsPreferenceEditor: React.FC<CareerGoalsPreferenceEditorProps> = ({
  data,
  onChange,
}) => {
  const [newGrowthArea, setNewGrowthArea] = React.useState('');

  const handleFieldChange = (field: keyof CareerGoals, value: any) => {
    onChange({ ...data, [field]: value });
  };

  const handleAddGrowthArea = () => {
    if (newGrowthArea.trim()) {
      const currentList = (data.growth_areas || []) as string[];
      onChange({
        ...data,
        growth_areas: [...currentList, newGrowthArea.trim()],
      });
      setNewGrowthArea('');
    }
  };

  const handleRemoveGrowthArea = (index: number) => {
    const currentList = (data.growth_areas || []) as string[];
    onChange({
      ...data,
      growth_areas: currentList.filter((_, i) => i !== index),
    });
  };

  return (
    <IonList className="preference-editor-list">
      <IonItem>
        <IonLabel position="stacked">
          <h3>Short-term Career Goals</h3>
          <p>What you want to achieve in the next 1-2 years</p>
        </IonLabel>
        <IonTextarea
          value={data.short_term || ''}
          placeholder="Describe your short-term goals..."
          rows={3}
          onIonChange={(e) => handleFieldChange('short_term', e.detail.value)}
        />
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">
          <h3>Long-term Career Goals</h3>
          <p>Where you see yourself in 3-5 years</p>
        </IonLabel>
        <IonTextarea
          value={data.long_term || ''}
          placeholder="Describe your career aspirations..."
          rows={4}
          onIonChange={(e) => handleFieldChange('long_term', e.detail.value)}
        />
      </IonItem>

      <IonItem>
        <IonLabel position="stacked">Growth Areas</IonLabel>
        <div className="chip-input-container">
          {data.growth_areas && data.growth_areas.length > 0 && (
            <div className="chip-list">
              {data.growth_areas.map((area, index) => (
                <IonChip key={index} color="primary">
                  {area}
                  <IonIcon
                    icon={closeCircle}
                    onClick={() => handleRemoveGrowthArea(index)}
                  />
                </IonChip>
              ))}
            </div>
          )}
          <div className="chip-input">
            <IonInput
              value={newGrowthArea}
              placeholder="e.g., Machine Learning, Team Leadership"
              onIonChange={(e) => setNewGrowthArea(e.detail.value || '')}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddGrowthArea();
                }
              }}
            />
            <IonButton onClick={handleAddGrowthArea} size="small">
              Add
            </IonButton>
          </div>
        </div>
      </IonItem>
    </IonList>
  );
};

export default CareerGoalsPreferenceEditor;
